import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';
import { Phone, User, Clock, Check, X, Send, ChevronRight } from 'lucide-react';

/**
 * Calling Mode — register-simple screen for reception/caller to log patient
 * calls + schedule next-call reminders. Three things, nothing else:
 *   1. Pick patient (type → autocomplete)
 *   2. Write note ("kya baat hui")
 *   3. Pick next-reminder in one tap (3/5/7/15/30 days, or none)
 * Top banner shows today's due callbacks with a one-tap "Done" button.
 */
const CallingModePage = ({ API_BASE, patientsList = [], currentUser }) => {
  const [dueList, setDueList] = useState([]);
  const [dueLoading, setDueLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [reminderDays, setReminderDays] = useState(5);
  const [waOptIn, setWaOptIn] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');

  const searchRef = useRef(null);
  const noteRef = useRef(null);

  // Keep focus flowing: patient → note → save, like filling out a register row.
  useEffect(() => { if (searchRef.current) searchRef.current.focus(); }, []);

  const loadDue = async () => {
    if (!API_BASE) return;
    setDueLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/calling-mode/today`);
      setDueList(r.data || []);
    } catch (e) { setDueList([]); }
    finally { setDueLoading(false); }
  };

  useEffect(() => { loadDue(); }, [API_BASE]);

  // Client-side filter from patientsList prop — instant, no API round-trip.
  // Falls back to /api/patients?search= if the prop is empty.
  const suggestions = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s || s.length < 2) return [];
    const list = patientsList || [];
    const rx = s.replace(/[.*+?^${}()|[\]\\]/g, '');
    return list
      .filter(p => {
        const name = (p.name || '').toLowerCase();
        const phone = (p.phone || '').toLowerCase();
        const uhid = (p.uhid || '').toLowerCase();
        return name.includes(rx) || phone.includes(rx) || uhid.includes(rx);
      })
      .slice(0, 8);
  }, [search, patientsList]);

  const pickPatient = (p) => {
    setSelected(p);
    setSearch(`${p.name || ''} (${p.phone || ''})`);
    setTimeout(() => { if (noteRef.current) noteRef.current.focus(); }, 50);
  };

  const resetForm = () => {
    setSearch('');
    setSelected(null);
    setNote('');
    setReminderDays(5);
    setWaOptIn(true);
    if (searchRef.current) searchRef.current.focus();
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const handleSave = async () => {
    if (!selected) { showToast('Pehle patient select karo'); return; }
    if (!note.trim()) { showToast('Note likhna zaroori hai'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_BASE}/calling-mode`, {
        patientId: selected._id,
        note: note.trim(),
        reminderDays: reminderDays,
        waOptIn,
      });
      showToast(reminderDays > 0
        ? `Saved — ${reminderDays} din baad reminder set ho gaya`
        : 'Saved — koi reminder nahi');
      resetForm();
      loadDue();
    } catch (e) {
      showToast('Save nahi hua: ' + (e.response?.data?.error || e.message));
    }
    setSaving(false);
  };

  const handleMarkDone = async (id) => {
    try {
      await axios.put(`${API_BASE}/calling-mode/${id}/done`, {});
      setDueList(prev => prev.filter(x => x._id !== id));
      showToast('Done!');
    } catch (e) { showToast('Update nahi hua'); }
  };

  const handleSnooze = async (id, days) => {
    try {
      await axios.put(`${API_BASE}/calling-mode/${id}/snooze`, { days });
      setDueList(prev => prev.filter(x => x._id !== id));
      showToast(`${days} din baad reminder`);
    } catch (e) { showToast('Snooze nahi hua'); }
  };

  const handleSkip = async (id) => {
    try {
      await axios.put(`${API_BASE}/calling-mode/${id}/skip`, {});
      setDueList(prev => prev.filter(x => x._id !== id));
      showToast('Skip kiya');
    } catch (e) { showToast('Skip nahi hua'); }
  };

  // ── Styles ──
  const dayBtn = (active) => ({
    padding: '14px 18px',
    fontSize: '1rem',
    fontWeight: 700,
    borderRadius: theme.radius,
    border: active ? `2px solid ${theme.primary}` : `1px solid ${theme.border}`,
    backgroundColor: active ? theme.primaryLight : theme.surface,
    color: active ? theme.primaryDark : theme.navy,
    cursor: 'pointer',
    minWidth: 100,
    transition: 'all 0.1s',
  });

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '8px 4px 24px' }}>
      {/* ── Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: '1.7rem', color: theme.navy, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Phone size={24} color={theme.primary} /> Calling Mode
        </h1>
        <p style={{ color: theme.textMuted, marginTop: 6, fontSize: '0.9rem' }}>
          Patient se baat hui — likho, reminder set karo, done.
        </p>
      </div>

      {/* ── Today's due banner ── */}
      <div style={{
        ...cardStyle,
        marginBottom: 18,
        padding: 0,
        overflow: 'hidden',
        border: `1px solid ${dueList.length ? theme.primary : theme.border}`,
      }}>
        <div style={{
          padding: '14px 20px',
          backgroundColor: dueList.length ? theme.primaryLight : theme.borderLight,
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}>
          <Clock size={18} color={theme.primary} />
          <strong style={{ color: theme.navy, fontSize: '1rem' }}>
            {dueLoading ? 'Loading…' :
              dueList.length === 0 ? 'Aaj koi reminder pending nahi hai 🎉' :
              `Aaj ${dueList.length} patient${dueList.length > 1 ? 's' : ''} ko call karni hai`}
          </strong>
        </div>
        {!dueLoading && dueList.length > 0 && (
          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {dueList.map(cb => (
              <DueRow key={cb._id} cb={cb}
                onDone={() => handleMarkDone(cb._id)}
                onSnooze={(d) => handleSnooze(cb._id, d)}
                onSkip={() => handleSkip(cb._id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── New-call form ── */}
      <div style={{ ...cardStyle, padding: 24 }}>
        {/* Patient search */}
        <label style={{ display: 'block', fontSize: '0.85rem', color: theme.textMuted, fontWeight: 600, marginBottom: 6 }}>
          PATIENT
        </label>
        <div style={{ position: 'relative' }}>
          <input
            ref={searchRef}
            value={search}
            onChange={e => { setSearch(e.target.value); if (selected) setSelected(null); }}
            placeholder="Naam ya phone number type karo…"
            style={{ ...inputStyle, fontSize: '1.05rem', padding: '14px 16px' }}
          />
          {!selected && suggestions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: theme.surface,
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radius,
              marginTop: 4,
              boxShadow: theme.shadowMd,
              maxHeight: 280,
              overflowY: 'auto',
              zIndex: 10,
            }}>
              {suggestions.map(p => (
                <div key={p._id}
                  onClick={() => pickPatient(p)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: `1px solid ${theme.borderLight}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = theme.primaryLight}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = theme.surface}
                >
                  <User size={16} color={theme.textMuted} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: theme.navy }}>{p.name}</div>
                    <div style={{ fontSize: '0.82rem', color: theme.textMuted }}>
                      {p.phone} {p.uhid ? `• ${p.uhid}` : ''}
                    </div>
                  </div>
                  <ChevronRight size={14} color={theme.textSubtle} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Note */}
        <label style={{ display: 'block', fontSize: '0.85rem', color: theme.textMuted, fontWeight: 600, marginTop: 18, marginBottom: 6 }}>
          KYA BAAT HUI?
        </label>
        <textarea
          ref={noteRef}
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="e.g. RCT ke baare me pooch raha tha, 5 din baad aayega..."
          rows={4}
          style={{ ...inputStyle, fontSize: '1rem', padding: 14, resize: 'vertical', minHeight: 100 }}
        />

        {/* Reminder days */}
        <label style={{ display: 'block', fontSize: '0.85rem', color: theme.textMuted, fontWeight: 600, marginTop: 18, marginBottom: 8 }}>
          NEXT REMINDER KAB?
        </label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { d: 3, label: '3 din' },
            { d: 5, label: '5 din' },
            { d: 7, label: '7 din' },
            { d: 15, label: '15 din' },
            { d: 30, label: '1 mahina' },
            { d: 0, label: 'Koi nahi' },
          ].map(opt => (
            <button key={opt.d} onClick={() => setReminderDays(opt.d)} style={dayBtn(reminderDays === opt.d)}>
              {opt.label}
            </button>
          ))}
        </div>

        {/* WA opt-in */}
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginTop: 18,
          padding: '10px 14px',
          backgroundColor: theme.sectionBlue,
          borderRadius: theme.radius,
          cursor: reminderDays > 0 ? 'pointer' : 'not-allowed',
          opacity: reminderDays > 0 ? 1 : 0.5,
        }}>
          <input type="checkbox"
            checked={waOptIn}
            disabled={reminderDays === 0}
            onChange={e => setWaOptIn(e.target.checked)}
            style={{ width: 18, height: 18 }}
          />
          <span style={{ fontSize: '0.9rem', color: theme.navy }}>
            Us din patient ko WhatsApp bhi bhejein (auto-reminder 9 AM)
          </span>
        </label>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={saving || !selected || !note.trim()}
          style={{
            ...btnStyle,
            marginTop: 22,
            width: '100%',
            padding: '16px',
            fontSize: '1.1rem',
            justifyContent: 'center',
            opacity: saving || !selected || !note.trim() ? 0.5 : 1,
          }}
        >
          <Send size={18} /> {saving ? 'Saving…' : 'SAVE & AGLA PATIENT'}
        </button>

        {/* Keyboard hint */}
        <div style={{ marginTop: 12, textAlign: 'center', fontSize: '0.78rem', color: theme.textSubtle }}>
          Tip: patient pick karne ke baad cursor note me aa jaata hai
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: theme.navy,
          color: 'white',
          padding: '12px 24px',
          borderRadius: theme.radiusLg,
          boxShadow: theme.shadowLg,
          fontSize: '0.95rem',
          zIndex: 1000,
        }}>
          {toast}
        </div>
      )}
    </div>
  );
};

const DueRow = ({ cb, onDone, onSnooze, onSkip }) => {
  const [showSnooze, setShowSnooze] = useState(false);
  return (
    <div style={{
      padding: '14px 20px',
      borderBottom: `1px solid ${theme.borderLight}`,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: theme.navy, fontSize: '0.98rem' }}>
          {cb.patientName} <span style={{ color: theme.textMuted, fontWeight: 400 }}>· {cb.phone}</span>
        </div>
        {cb.note && (
          <div style={{
            color: theme.textMuted,
            fontSize: '0.85rem',
            marginTop: 2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {cb.note}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {showSnooze ? (
          <>
            {[1, 3, 7].map(d => (
              <button key={d} onClick={() => { onSnooze(d); setShowSnooze(false); }}
                style={{ padding: '6px 10px', fontSize: '0.82rem', border: `1px solid ${theme.border}`, borderRadius: 6, backgroundColor: theme.surface, cursor: 'pointer' }}>
                +{d}d
              </button>
            ))}
            <button onClick={() => setShowSnooze(false)}
              style={{ padding: '6px 10px', fontSize: '0.82rem', border: 'none', background: 'transparent', cursor: 'pointer', color: theme.textMuted }}>
              <X size={14} />
            </button>
          </>
        ) : (
          <>
            <button onClick={onDone} title="Call ho gayi"
              style={{ padding: '8px 12px', fontSize: '0.85rem', fontWeight: 600, backgroundColor: theme.success, color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Check size={14} /> Done
            </button>
            <button onClick={() => setShowSnooze(true)} title="Snooze"
              style={{ padding: '8px 10px', fontSize: '0.85rem', border: `1px solid ${theme.border}`, backgroundColor: theme.surface, borderRadius: 6, cursor: 'pointer' }}>
              Baad me
            </button>
            <button onClick={onSkip} title="Skip"
              style={{ padding: '8px 10px', fontSize: '0.85rem', border: 'none', background: 'transparent', color: theme.textMuted, cursor: 'pointer' }}>
              <X size={14} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CallingModePage;
